import { Stack, Group, Text, Box } from '@repletoryreach/sdk-ui';
import { StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import type { admin } from '@repletoryreach/sdk-api';
import { formatDate, formatRelative } from '../../../../lib/format';
import { TenantName } from '../../../../components/TenantName';
import { cycleLabel, statusLabel, statusTone, PlanFlags } from './SubscriptionsTable';
import { useT } from '../../../../i18n/I18nProvider';

interface Props {
  records: admin.AdminSubscription[];
  planLabel: (planId: string) => string;
  planPrice: (planId: string) => { amount_minor: number; currency: string } | undefined;
  onSelect: (s: admin.AdminSubscription) => void;
}

/** Vertical list view. Rows navigate to the tenant, matching the table. */
export function SubscriptionsList({ records, planLabel, planPrice, onSelect }: Props) {
  const t = useT();
  return (
    <Stack gap="xs">
      {records.map((s) => (
        <button key={s.id} type="button" className="rr-list-row" onClick={() => onSelect(s)}>
          <Group gap="md" align="center" wrap="nowrap" style={{ width: '100%' }}>
            <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
              <Group gap="xs" align="baseline" wrap="nowrap">
                <Text role="subtitle" fw={600} truncate><code>{planLabel(s.plan_id)}</code></Text>
                <TenantName id={s.organisation_id} subtle />
              </Group>
              <Text role="caption" c="var(--rr-color-text-muted)">
                {t('billing.subscriptions.list.meta', { started: formatDate(s.started_at), cycle: cycleLabel(s) })}
                {s.cancelled_at ? ` · ${t('billing.subscriptions.list.cancelled', { when: formatDate(s.cancelled_at) })}` : ''}
              </Text>
              <PlanFlags s={s} paid={(planPrice(s.plan_id)?.amount_minor ?? 0) > 0} planLabel={planLabel} />
              {s.status === 'past_due' && (
                <Text role="caption" c="var(--rr-color-error)">
                  {t('billing.subscriptions.pastDueSince', { when: formatRelative(s.billing_cycle_end) })}
                </Text>
              )}
            </Stack>
            <Box style={{ flexShrink: 0 }}>
              <StatusBadge status={statusTone(s.status)}>{statusLabel(t, s.status)}</StatusBadge>
            </Box>
          </Group>
        </button>
      ))}
    </Stack>
  );
}
